import { useState } from "react";
import { cn } from "@/lib/utils";
import { PulsingDot } from "@/components/PulsingDot";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

export interface SeriesConfig {
  key: string;
  name: string;
  color: string; // hsl(var(--...)) ou hex
}

type ChartMode = "bar" | "line";

interface MultiSeriesChartProps {
  data: unknown[];
  series: SeriesConfig[];
  title: string;
  subtitle?: string;
  onPointClick?: (payload: Record<string, unknown>) => void;
  formatValue?: (v: number) => string;
  defaultMode?: ChartMode;
  allowModeToggle?: boolean;
  height?: number;
  /**
   * Quando true E há `onPointClick`, todas as séries ganham PulsingDot.
   * Default `false` — só a série primária pulsa (evita poluição visual
   * quando há muitas séries).
   */
  pulseAllSeries?: boolean;
}

export function MultiSeriesChart({
  data,
  series,
  title,
  subtitle,
  onPointClick,
  formatValue,
  defaultMode = "line",
  allowModeToggle = true,
  height = 260,
  pulseAllSeries = false,
}: MultiSeriesChartProps) {
  const [mode, setMode] = useState<ChartMode>(defaultMode);
  const fmt = formatValue ?? ((v: number) => v.toLocaleString("pt-BR"));
  const chartData = data as Array<Record<string, number | string>>;

  return (
    <section className="rounded-lg border border-border-subtle bg-bg-card p-6 space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold tracking-tight text-text-primary">{title}</h2>
          {subtitle && <p className="text-[13px] text-text-muted">{subtitle}</p>}
        </div>
        {allowModeToggle && <ChartModeToggle mode={mode} onChange={setMode} />}
      </div>

      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          {mode === "bar" ? (
            <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="hsl(var(--border-subtle))" vertical={false} strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={(v) => (typeof v === "string" ? new Date(v).getDate().toString() : String(v))}
                stroke="hsl(var(--text-muted))"
                fontSize={10}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                stroke="hsl(var(--text-muted))"
                fontSize={10}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v) => fmt(Number(v))}
                width={55}
              />
              <Tooltip content={<SharedTooltip series={series} formatValue={fmt} />} cursor={{ fill: "hsl(var(--bg-card-hover))" }} />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 12 }} />
              {series.map((s) => (
                <Bar
                  key={s.key}
                  dataKey={s.key}
                  name={s.name}
                  fill={s.color}
                  radius={[4, 4, 0, 0]}
                  isAnimationActive={false}
                  onClick={(d) => onPointClick?.((d as { payload?: Record<string, unknown> })?.payload ?? (d as Record<string, unknown>))}
                  style={onPointClick ? { cursor: "pointer" } : undefined}
                />
              ))}
            </BarChart>
          ) : (
            <LineChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="hsl(var(--border-subtle))" vertical={false} strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={(v) => (typeof v === "string" ? new Date(v).getDate().toString() : String(v))}
                stroke="hsl(var(--text-muted))"
                fontSize={10}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                stroke="hsl(var(--text-muted))"
                fontSize={10}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v) => fmt(Number(v))}
                width={55}
              />
              <Tooltip content={<SharedTooltip series={series} formatValue={fmt} />} cursor={{ stroke: "hsl(var(--border-strong))" }} />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 12 }} />
              {series.map((s, i) => {
                const isPrimary = i === 0;
                const usePulse = !!onPointClick && (pulseAllSeries || isPrimary);
                return (
                  <Line
                    key={s.key}
                    type="monotone"
                    dataKey={s.key}
                    name={s.name}
                    stroke={s.color}
                    strokeWidth={2.2}
                    dot={
                      usePulse
                        ? ((props: unknown) => (
                            <PulsingDot
                              {...(props as Record<string, unknown>)}
                              color={s.color}
                              interactive
                              onClick={(p) => onPointClick?.(p as Record<string, unknown>)}
                            />
                          )) as unknown as boolean
                        : { r: 3, fill: s.color, strokeWidth: 0 }
                    }
                    activeDot={{
                      r: 5,
                      style: onPointClick ? { cursor: "pointer" } : undefined,
                      onClick: onPointClick
                        ? (_e: unknown, payload: unknown) =>
                            onPointClick((payload as { payload?: Record<string, unknown> })?.payload ?? (payload as Record<string, unknown>))
                        : undefined,
                    }}
                    isAnimationActive={false}
                  />
                );
              })}
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>
    </section>
  );
}

function ChartModeToggle({ mode, onChange }: { mode: ChartMode; onChange: (m: ChartMode) => void }) {
  return (
    <div className="flex items-center rounded-md border border-border-subtle bg-bg-primary p-0.5">
      {(["bar", "line"] as const).map((m) => (
        <button
          key={m}
          type="button"
          onClick={() => onChange(m)}
          className={cn(
            "px-3 py-1 text-xs font-medium transition",
            mode === m
              ? "rounded bg-bg-card text-text-primary shadow-sm"
              : "text-text-muted hover:text-text-secondary"
          )}
        >
          {m === "bar" ? "Barras" : "Linha"}
        </button>
      ))}
    </div>
  );
}

type TooltipPayload = {
  name: string;
  value: number;
  dataKey: string;
  color: string;
  payload: Record<string, unknown>;
};

function SharedTooltip({
  active,
  payload,
  label,
  series,
  formatValue,
}: {
  active?: boolean;
  payload?: TooltipPayload[];
  label?: string;
  series: SeriesConfig[];
  formatValue: (v: number) => string;
}) {
  if (!active || !payload?.length) return null;
  const dateStr = String(payload[0].payload.date ?? label ?? "");
  const dateLabel = new Date(dateStr).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  return (
    <div className="min-w-[180px] rounded-md border border-border-strong bg-bg-card px-3 py-2 shadow-lg">
      <div className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-text-muted">{dateLabel}</div>
      <div className="space-y-1">
        {series.map((s) => {
          const entry = payload.find((p) => p.dataKey === s.key);
          if (!entry) return null;
          return (
            <div key={s.key} className="flex items-center gap-2 text-sm">
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />
              <span className="text-text-secondary">{s.name}</span>
              <span className="ml-auto font-semibold text-text-primary">{formatValue(Number(entry.value))}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
